
import React, { useState, useEffect } from 'react';
import { X, Save, Unlink } from 'lucide-react';
import { ReconciliationRecord, MatchStatus } from '../../types';
import ConfirmDialog from './ConfirmDialog';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  record: ReconciliationRecord | null;
  onSave: (record: ReconciliationRecord) => Promise<void>;
  onUnlink?: (record: ReconciliationRecord) => Promise<void>;
}

const ReconciliationEditModal: React.FC<Props> = ({ isOpen, onClose, record, onSave, onUnlink }) => {
  const [formData, setFormData] = useState<ReconciliationRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [billingText, setBillingText] = useState('');
  const [depositText, setDepositText] = useState('');
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  useEffect(() => {
    if (isOpen && record) {
      setFormData({ ...record });
      setBillingText(String(record.billing_amount || 0));
      setDepositText(String(record.deposit_amount || 0));
      setConfirmUnlinkOpen(false);
    }
  }, [isOpen, record]);

  if (!isOpen || !formData) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({ ...formData, delete_match: false } as any);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  // 紐付けをやめる対象：照合済みレコード（matched タイプ）のみ
  const canUnlink = !!onUnlink && formData.type === 'matched';
  const hasBothSides = !!(formData.billing_id && formData.deposit_id);

  const buildUnlinkMessage = () => {
    const clientLabel = formData.client_name || '名称不明';
    const remitterLabel = formData.remitter_name || '名称不明';
    if (hasBothSides) {
      return [
        `「${clientLabel}」と振込人名「${remitterLabel}」の紐付けを解除します。`,
        ``,
        `解除後の振り分け先：`,
        `・請求データ ¥${(formData.billing_amount || 0).toLocaleString()} → 「未照合（請求）」タブ`,
        `・入金データ ¥${(formData.deposit_amount || 0).toLocaleString()} → 「入金のみ」タブ`,
        ``,
        `※ 同じ組み合わせを今後自動で紐付けないように記録します。`,
      ].join('\n');
    }
    return [
      `振込人名「${remitterLabel}」の紐付けを解除します。`,
      ``,
      `解除後は「入金のみ」タブに振り分けられます。`,
      `※ 後から「手動照合」で正しい請求と紐付けできます。`,
    ].join('\n');
  };

  const handleUnlink = async () => {
    if (!onUnlink || !formData) return;
    setIsUnlinking(true);
    try {
      await onUnlink(formData);
      setConfirmUnlinkOpen(false);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-0 overflow-hidden animate-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">照合データの修正</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={20}/></button>
        </div>

        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-2">
              <p className="font-bold text-blue-900 text-sm">{formData.client_name || formData.remitter_name || '名称不明'}</p>
              <p className="text-blue-700 text-xs mt-1">得意先CD：{formData.client_code || '-'}</p>
              {formData.remitter_name && formData.client_name && formData.remitter_name !== formData.client_name && (
                <p className="text-blue-600 text-xs mt-0.5">振込人名：{formData.remitter_name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-black text-gray-400 uppercase mb-1 tracking-wider">照合ステータス</label>
              <select
                value={formData.status}
                onChange={e => setFormData({...formData, status: e.target.value as MatchStatus})}
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none bg-white shadow-sm"
              >
                {Object.values(MatchStatus).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase mb-1 tracking-wider">請求額</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={billingText}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9-]/g, '');
                    setBillingText(raw);
                    const v = raw === '' || raw === '-' ? 0 : parseInt(raw);
                    setFormData({...formData, billing_amount: v, difference: v - formData.deposit_amount});
                  }}
                  onBlur={() => setBillingText(String(formData.billing_amount))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-right font-mono bg-white focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase mb-1 tracking-wider">入金額</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={depositText}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9-]/g, '');
                    setDepositText(raw);
                    const v = raw === '' || raw === '-' ? 0 : parseInt(raw);
                    setFormData({...formData, deposit_amount: v, difference: formData.billing_amount - v});
                  }}
                  onBlur={() => setDepositText(String(formData.deposit_amount))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-right font-mono bg-white focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-gray-400 uppercase mb-1 tracking-wider">備考 / 内部メモ</label>
              <textarea
                value={formData.notes || ''}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm h-20 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                placeholder="照合に関するメモを入力..."
              />
            </div>

            <div className="pt-4 flex justify-between items-center gap-2 border-t mt-2">
              <div>
                {canUnlink && (
                  <button
                    type="button"
                    onClick={() => setConfirmUnlinkOpen(true)}
                    disabled={isSaving || isUnlinking}
                    className="px-3 py-2 text-xs border border-red-200 rounded-lg text-red-600 font-bold hover:bg-red-50 transition flex items-center gap-1.5 disabled:opacity-50"
                    title="この紐付けを解除します"
                  >
                    <Unlink size={14}/> 紐付けをやめる
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 font-bold hover:bg-gray-50 transition">キャンセル</button>
                <button type="submit" disabled={isSaving || isUnlinking} className="px-8 py-2 text-sm bg-blue-600 text-white rounded-lg font-black hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 shadow-md">
                  <Save size={16}/> 保存して反映
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmUnlinkOpen}
        title="紐付けをやめますか？"
        message={buildUnlinkMessage()}
        type="danger"
        confirmText={isUnlinking ? '処理中...' : '紐付けをやめる'}
        cancelText="キャンセル"
        onConfirm={handleUnlink}
        onCancel={() => setConfirmUnlinkOpen(false)}
      />
    </div>
  );
};

export default ReconciliationEditModal;
